import React from 'react'
import { ChevronDown } from 'lucide-react'
import { AppLayout } from '../../components/layout/AppLayout'
import { apiFetch } from '../../lib/api-client'
import { fmtDate, whoPaysLabel } from './format'
import { WhoPaysPanel, type WhoPaysFields } from './WhoPaysPanel'

interface Henvendelse extends WhoPaysFields {
  category: string
  likely_cause: string | null
  summary: string
  description: string | null
  urgency: string
  status: string
  unit_no: string | null
  renter_name: string | null
  created_at: string | null
}

const CATEGORY_LABELS: Record<string, string> = {
  vvs: 'VVS',
  varme: 'Varme',
  el: 'El',
  hvidevarer: 'Hvidevarer',
  leje_betaling: 'Leje / betaling',
  generel: 'Generel',
}

const STATUS_LABELS: Record<string, string> = {
  ny: 'Ny',
  set: 'Set',
  loest: 'Løst',
}

const COLUMN_COUNT = 8

function WhoPaysCell({ r }: { r: Henvendelse }) {
  if (r.who_pays) {
    return <span style={{ color: '#065f46', fontWeight: 600 }}>{whoPaysLabel(r.who_pays)}</span>
  }
  if (r.who_pays_suggestion) {
    return (
      <span style={{ color: '#6b7280' }}>
        Forslag: {whoPaysLabel(r.who_pays_suggestion)}
      </span>
    )
  }
  return <span style={{ color: '#9ca3af' }}>—</span>
}

export function HenvendelserPage() {
  const [rows, setRows] = React.useState<Henvendelse[]>([])
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)
  const [openId, setOpenId] = React.useState<string | null>(null)

  const load = React.useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await apiFetch<Henvendelse[]>('/api/v1/henvendelser')
      setRows(data)
    } catch (e: unknown) {
      setError((e instanceof Error ? e.message : null) ?? 'Kunne ikke hente henvendelser.')
    } finally {
      setLoading(false)
    }
  }, [])

  // One-shot mount-time data load.
  React.useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void load()
  }, [load])

  async function changeStatus(id: string, status: string) {
    // Optimistic update.
    setRows((rs) => rs.map((r) => (r.id === id ? { ...r, status } : r)))
    try {
      await apiFetch(`/api/v1/henvendelser/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ status }),
      })
    } catch {
      void load() // revert by reloading on failure
    }
  }

  function patchRow(id: string, patch: Partial<WhoPaysFields>) {
    setRows((rs) => rs.map((r) => (r.id === id ? { ...r, ...patch } : r)))
  }

  return (
    <AppLayout>
      <div style={{ padding: '0 0 24px 0' }}>
        <div style={{ fontSize: 20, fontWeight: 700, color: '#111827', marginBottom: 3 }}>
          Henvendelser
        </div>
        <div style={{ fontSize: 14, color: '#6b7280' }}>
          Sager som lejere har oprettet via boligportalen
        </div>
      </div>

      <div
        style={{
          background: '#ffffff',
          borderRadius: 10,
          border: '1px solid #eef0f4',
          overflow: 'hidden',
        }}
      >
        {loading ? (
          <div style={{ padding: 32, fontSize: 14, color: '#9ca3af' }}>Henter…</div>
        ) : error ? (
          <div style={{ padding: 32, fontSize: 14, color: '#dc2626' }}>{error}</div>
        ) : rows.length === 0 ? (
          <div style={{ padding: 32, fontSize: 14, color: '#9ca3af' }}>
            Ingen henvendelser endnu.
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #e8eaed', textAlign: 'left' }}>
                <th style={thStyle}>Kategori</th>
                <th style={thStyle}>Lejer</th>
                <th style={thStyle}>Lejemål</th>
                <th style={thStyle}>Sag</th>
                <th style={thStyle}>Hastende</th>
                <th style={thStyle}>Modtaget</th>
                <th style={thStyle}>Status</th>
                <th style={thStyle}>Hvem betaler?</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const open = openId === r.id
                return (
                <React.Fragment key={r.id}>
                <tr style={{ borderBottom: open ? 'none' : '1px solid #f3f4f6' }}>
                  <td style={tdStyle}>{CATEGORY_LABELS[r.category] ?? r.category}</td>
                  <td style={tdStyle}>{r.renter_name ?? '—'}</td>
                  <td style={tdStyle}>{r.unit_no ?? '—'}</td>
                  <td style={{ ...tdStyle, maxWidth: 320 }}>
                    <div style={{ fontWeight: 600, color: '#111827' }}>{r.summary}</div>
                    {r.likely_cause && (
                      <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
                        Mulig årsag: {r.likely_cause}
                      </div>
                    )}
                  </td>
                  <td style={tdStyle}>
                    {r.urgency === 'akut' ? (
                      <span
                        style={{
                          fontSize: 12,
                          fontWeight: 700,
                          color: '#dc2626',
                          background: '#fee2e2',
                          padding: '2px 8px',
                          borderRadius: 20,
                        }}
                      >
                        AKUT
                      </span>
                    ) : (
                      <span style={{ fontSize: 12, color: '#6b7280' }}>Normal</span>
                    )}
                  </td>
                  <td style={tdStyle}>{fmtDate(r.created_at)}</td>
                  <td style={tdStyle}>
                    <select
                      value={r.status}
                      onChange={(e) => void changeStatus(r.id, e.target.value)}
                      style={{
                        padding: '6px 10px',
                        borderRadius: 7,
                        border: '1px solid #d1d5db',
                        fontSize: 13,
                        background: '#fff',
                        cursor: 'pointer',
                      }}
                    >
                      {Object.entries(STATUS_LABELS).map(([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td style={tdStyle}>
                    <button
                      type="button"
                      aria-expanded={open}
                      aria-controls={`who-pays-${r.id}`}
                      onClick={() => setOpenId(open ? null : r.id)}
                      style={toggleStyle}
                    >
                      <WhoPaysCell r={r} />
                      <ChevronDown
                        size={14}
                        aria-hidden
                        style={{
                          color: '#9ca3af',
                          flexShrink: 0,
                          transform: open ? 'rotate(180deg)' : 'none',
                          transition: 'transform 0.15s',
                        }}
                      />
                    </button>
                  </td>
                </tr>
                {open && (
                  <tr id={`who-pays-${r.id}`} style={{ borderBottom: '1px solid #f3f4f6', background: '#fafbfc' }}>
                    <td colSpan={COLUMN_COUNT} style={{ padding: '4px 16px 18px' }}>
                      {r.description && (
                        <p style={{ margin: '8px 0 12px', fontSize: 13, color: '#374151', lineHeight: 1.55, maxWidth: 720, whiteSpace: 'pre-wrap' }}>
                          {r.description}
                        </p>
                      )}
                      <WhoPaysPanel row={r} onChange={(patch) => patchRow(r.id, patch)} />
                    </td>
                  </tr>
                )}
                </React.Fragment>
                )
              })}
            </tbody>
          </table>
        )}
      </div>
    </AppLayout>
  )
}

const thStyle: React.CSSProperties = {
  padding: '12px 16px',
  fontSize: 12,
  fontWeight: 600,
  color: '#6b7280',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
}

const tdStyle: React.CSSProperties = {
  padding: '14px 16px',
  color: '#374151',
  verticalAlign: 'top',
}

const toggleStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '4px 8px',
  margin: '-4px -8px',
  border: 'none',
  borderRadius: 6,
  background: 'transparent',
  fontSize: 13,
  fontFamily: 'inherit',
  whiteSpace: 'nowrap',
  cursor: 'pointer',
}
